import { useState } from "react";
import { Link } from "react-router-dom";
import {
  integrationsContent,
  integrationCategories,
  integrations,
  getIntegrationsByCategory,
  site,
} from "@/content";
import LightNavbar from "@/pages/main-light/components/LightNavbar";
import LightFooter from "@/pages/main-light/components/LightFooter";
import IntegrationLeadForm from "./components/IntegrationLeadForm";
import SeoHead from "@/components/feature/SeoHead";
import { RichTemplate, interpolate } from "@/lib/rich";
import { useSurfaces } from "@/theme/surfaces";

/**
 * Каталог интеграций. Адрес один (`/integrations`) — тема переключается
 * без смены URL, разметка общая, цвета приходят из токенов.
 */
export default function IntegrationsPage() {
  const { seo, hero, faq, lead } = integrationsContent;
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const t = useSurfaces();
  const total = integrations.length;

  return (
    <>
      <SeoHead
        title={interpolate(seo.title, { count: total })}
        description={interpolate(seo.description, { count: total })}
        keywords={seo.keywords}
        canonicalPath={seo.canonicalPath}
      />

      <div className={t.page}>
        <LightNavbar />

        {/* Hero */}
        <section className="pt-32 pb-14 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className={`absolute top-[10%] right-[5%] w-[500px] h-[500px] rounded-full blur-[140px] ${t.glowA}`} />
            <div className={`absolute bottom-[5%] left-[-5%] w-[400px] h-[400px] rounded-full blur-[130px] ${t.glowB}`} />
            <div className={`absolute top-[40%] left-[35%] w-[350px] h-[350px] rounded-full blur-[130px] ${t.glowC}`} />
          </div>

          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <nav className="flex items-center gap-2 text-sm mb-8 whitespace-nowrap overflow-x-auto">
              <Link to="/" className={`transition-colors cursor-pointer ${t.crumbLink}`}>
                {site.breadcrumbs.home}
              </Link>
              <i className={`ri-arrow-right-s-line ${t.crumbSep}`}></i>
              <span className={`font-medium ${t.crumbCurrent}`}>{site.breadcrumbs.integrations}</span>
            </nav>

            <div className="max-w-3xl">
              <div
                className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold mb-6 ${t.badge}`}
              >
                <span className={`w-2 h-2 rounded-full inline-block ${t.badgeIcon}`}></span>
                {interpolate(hero.badge, { count: total })}
              </div>

              <h1
                className={`text-3xl md:text-5xl font-extrabold leading-[1.12] mb-5 ${t.h1}`}
              >
                <RichTemplate
                  template={hero.title}
                  values={{ count: total }}
                  accentClassName={t.accent}
                />
              </h1>

              <p className={`text-lg md:text-xl leading-relaxed mb-8 ${t.body}`}>
                {hero.text}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={hero.primaryCta.href}
                  className={`px-8 py-4 rounded-xl font-bold text-base transition-colors cursor-pointer text-center whitespace-nowrap ${t.ctaPrimary}`}
                >
                  {hero.primaryCta.label}
                </a>
                <a
                  href={hero.secondaryCta.href}
                  className={`px-8 py-4 rounded-xl font-semibold text-base transition-colors cursor-pointer text-center whitespace-nowrap ${t.ctaSecondary}`}
                >
                  {hero.secondaryCta.label}
                </a>
              </div>
            </div>

            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 max-w-4xl">
              {hero.stats.map((stat) => (
                <div key={stat.label}>
                  <div className={`text-2xl md:text-3xl font-extrabold ${t.h2}`}>
                    {interpolate(stat.value, { count: total })}
                  </div>
                  <div className={`text-xs md:text-sm mt-1 ${t.muted}`}>
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Быстрый переход по категориям */}
        <section id="categories" className="pb-4 scroll-mt-24">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-wrap gap-2">
              {integrationCategories.map((category) => (
                <a
                  key={category.slug}
                  href={`#${category.slug}`}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${t.chip}`}
                >
                  <i className={`${category.icon} text-base`}></i>
                  {category.title}
                  <span className={`font-normal ${t.chipCount}`}>
                    {getIntegrationsByCategory(category.slug).length}
                  </span>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Каталог */}
        <section className="py-14">
          <div className="max-w-7xl mx-auto px-6 space-y-16">
            {integrationCategories.map((category) => {
              const items = getIntegrationsByCategory(category.slug);
              return (
                <div key={category.slug} id={category.slug} className="scroll-mt-24">
                  <div className="flex items-start gap-4 mb-7">
                    <div
                      className={`w-12 h-12 flex items-center justify-center rounded-xl shrink-0 ${t.categoryIconBox}`}
                    >
                      <i className={`${category.icon} text-xl ${t.categoryIcon}`}></i>
                    </div>
                    <div>
                      <h2 className={`text-2xl md:text-3xl font-extrabold mb-1.5 ${t.h2}`}>
                        {category.title}
                      </h2>
                      <p
                        className={`text-sm md:text-base leading-relaxed max-w-3xl ${t.body}`}
                      >
                        {category.description}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {items.map((item) => (
                      <Link
                        key={item.slug}
                        to={`/integrations/${item.slug}`}
                        className={`group rounded-2xl p-6 flex flex-col hover:-translate-y-1 transition-all duration-300 cursor-pointer ${t.card}`}
                      >
                        <div className="flex items-start justify-between gap-3 mb-4">
                          <div
                            className={`w-11 h-11 flex items-center justify-center rounded-xl transition-colors ${t.cardIconBox}`}
                          >
                            <i
                              className={`${item.icon} text-lg transition-colors ${t.cardIcon}`}
                            ></i>
                          </div>
                          <span
                            className={`text-xs font-semibold whitespace-nowrap pt-1 ${t.cardPrice}`}
                          >
                            {item.price}
                          </span>
                        </div>

                        <h3 className={`text-base font-bold mb-2 ${t.cardTitle}`}>
                          {item.title}
                        </h3>
                        <p className={`text-sm leading-relaxed mb-4 flex-1 ${t.cardText}`}>
                          {item.shortDesc}
                        </p>

                        <span
                          className={`inline-flex items-center gap-1.5 text-sm font-semibold group-hover:gap-2.5 transition-all whitespace-nowrap ${t.cardLink}`}
                        >
                          {integrationsContent.detail.cardLink}
                          <i className="ri-arrow-right-line text-sm"></i>
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* FAQ */}
        <section className={`py-20 ${t.sectionAlt}`}>
          <div className="max-w-3xl mx-auto px-6">
            <div className="text-center mb-12">
              <span
                className={`inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase mb-4 ${t.badge}`}
              >
                {faq.badge}
              </span>
              <h2 className={`text-2xl md:text-4xl font-extrabold mb-3 ${t.h2}`}>
                {faq.title}
              </h2>
              {faq.subtitle && (
                <p className={`text-base ${t.body}`}>{faq.subtitle}</p>
              )}
            </div>

            <div className="space-y-3">
              {faq.items.map((item, index) => {
                const isOpen = openFaq === index;
                return (
                  <div
                    key={item.q}
                    className={`rounded-2xl border transition-all duration-200 overflow-hidden ${
                      isOpen ? t.faqItemOpen : t.faqItemClosed
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : index)}
                      aria-expanded={isOpen}
                      className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer"
                    >
                      <span
                        className={`font-semibold text-sm md:text-base ${
                          isOpen ? t.faqQuestionOpen : t.faqQuestionClosed
                        }`}
                      >
                        {item.q}
                      </span>
                      <span
                        className={`w-7 h-7 flex items-center justify-center rounded-full shrink-0 transition-all ${
                          isOpen ? t.faqToggleOpen : t.faqToggleClosed
                        }`}
                      >
                        <i
                          className={`ri-add-line text-sm ${
                            isOpen ? t.faqToggleIconOpen : t.faqToggleIconClosed
                          }`}
                        ></i>
                      </span>
                    </button>
                    {isOpen && (
                      <div className="px-6 pb-5">
                        <p className={`text-sm leading-relaxed ${t.body}`}>
                          {item.a}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Заявка */}
        <section id="lead" className="py-20 scroll-mt-24 relative overflow-hidden">
          <div className={`absolute top-[5%] right-[0%] w-[400px] h-[400px] rounded-full blur-[120px] pointer-events-none ${t.glowA}`} />
          <div className={`absolute bottom-[10%] left-[-5%] w-[320px] h-[320px] rounded-full blur-[110px] pointer-events-none ${t.glowB}`} />

          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <div>
                <h2 className={`text-2xl md:text-4xl font-extrabold leading-tight mb-5 ${t.h2}`}>
                  <RichTemplate
                    template={lead.title}
                    values={{}}
                    accentClassName={t.accent}
                  />
                </h2>
                <p className={`text-lg leading-relaxed mb-8 ${t.body}`}>
                  {lead.text}
                </p>

                <div className="flex flex-col gap-4 mb-8">
                  {lead.bullets.map((bullet) => (
                    <div key={bullet.text} className="flex items-center gap-3">
                      <div
                        className={`w-9 h-9 flex items-center justify-center rounded-full shrink-0 ${t.bulletCircle}`}
                      >
                        <i className={`${bullet.icon} text-sm ${t.bulletIcon}`}></i>
                      </div>
                      <span className={`text-sm ${t.bulletText}`}>{bullet.text}</span>
                    </div>
                  ))}
                </div>

                <Contacts />
              </div>

              <div className="w-full max-w-lg lg:ml-auto">
                <IntegrationLeadForm
                  formName="integrations-catalog"
                  title={lead.formTitle}
                  subtitle={lead.formSubtitle}
                />
              </div>
            </div>
          </div>
        </section>

        <LightFooter />
      </div>
    </>
  );
}

function Contacts() {
  const t = useSurfaces();
  return (
    <div className={`flex flex-col gap-2.5 text-sm ${t.muted}`}>
      <a
        href={site.contacts.phoneHref}
        className={`inline-flex items-center gap-2 transition-colors cursor-pointer ${t.contactLink}`}
      >
        <i className="ri-phone-line text-sm"></i>
        {site.contacts.phone}
      </a>
      <a
        href={site.contacts.emailHref}
        className={`inline-flex items-center gap-2 transition-colors cursor-pointer ${t.contactLink}`}
      >
        <i className="ri-mail-line text-sm"></i>
        {site.contacts.email}
      </a>
    </div>
  );
}
